// poEditDraft.ts — the draft state and the diff behind the PO detail page's
// edit mode. Same split as poImportDraft.ts: the page renders, this module
// decides what changed.
//
// WHY A DIFF AND NOT A WHOLE-DOCUMENT SAVE
//
// PATCH /procurement/purchase-orders/:id treats an omitted field as "leave it
// alone" and an omitted line as "leave that line alone" (see the doc comment on
// PurchaseOrdersService.update). Sending the entire document back would work,
// but it also means every save rewrites every field and bumps every line's
// updated_at, so the audit trail stops distinguishing "someone corrected the
// ship date" from "someone opened the editor and pressed Save". Sending only
// the deltas keeps that signal.
//
// EVERY DRAFT FIELD IS A STRING. They are <input> values; an in-progress
// quantity is legitimately '' or '1' on the way to '12', and a number-typed
// draft has to invent something for those states. The conversion back to the
// API's integers, cents and nulls happens once, here, at save time.
//
// WHAT IS DELIBERATELY NOT EDITABLE HERE
//   vendor_id      — a vendor swap re-snapshots terms server-side; that belongs
//                    with a vendor picker, not a text field.
//   over_receipt_pct — not shown on the read view either. Adding an input for a
//                    field the page never displays would be the only way to
//                    discover it exists.
//   part_catalog_id / line_type — changing what a line IS, versus how much of it
//                    was ordered, is a different act. Remove the line and put
//                    the right one on instead. DUPLICATING a line is the one way
//                    a new line reaches this page, and it copies both fields
//                    rather than letting anyone pick them.
//   quantity_received — receiving owns it. The API ignores it on a patch.
//
// DUPLICATED LINES
//
// A buyer ordering the same SKU against two delivery dates needs two lines that
// differ only in the date, and the header's single expected_date cannot say
// that. So a row can be duplicated: the copy is STAGED, editable, and written
// only when Save is pressed, which is the shape Odoo and ERPNext both use for
// their order-line grids. It travels in the same `lines` array as every other
// edit, WITHOUT an id, because that is already how the API says "append this"
// (see PurchaseOrdersService.update). No new endpoint, no second save path.
//
// A staged copy exists only in the page's state. It has no database row, so its
// synthetic id must never reach `remove_line_ids` — discarding one drops it from
// the array instead. isNewLineId() below is what keeps those two apart.

import {
  centsToDollarsInput,
  dollarsToCents,
  type PoDetail,
  type PoEditable,
  type PoLine,
  type PoLineInput,
  type UpdatePoInput,
} from './poApi';
import { type StagedDiscount, type StagedLine } from './PoLineEntry';
import { isPaymentTermsBasis } from './paymentClock';
import { insertPinned } from './LineOrder';

// ---------------------------------------------------------------------------
// Draft shapes
// ---------------------------------------------------------------------------

/** Header fields the editor exposes. Names match the API's columns so the diff
 * below can key straight off them. */
export interface HeaderDraft {
  order_date: string;
  ship_date: string;
  expected_date: string;
  terms: string;
  /** The payment clock, as typed. Both soft, like the prose they compute. */
  terms_days: string;
  terms_basis: string;
  quote_number: string;
  ship_via: string;
  ship_to_name: string;
  ship_to_attn: string;
  ship_to_address: string;
  /** Internal. Approvals and the historical import write here too, and it never
   * prints. */
  notes: string;
  /** Vendor-facing. The only prose the generated PDF renders. */
  vendor_notes: string;
  /** The order's ONE discount (migration 110), or null for none. Not a typed
   * field: it is staged whole from the entry row (the Discount kind) or removed
   * from the strip under the lines, so it is held as the shape the row hands
   * over rather than as input strings. */
  discount: StagedDiscount | null;
}

export interface LineDraft {
  quantity_expected: string;
  /** Dollars, as typed. dollarsToCents at save time. */
  unit_cost: string;
  expected_arrival_date: string;
  notes: string;
  /** Why the quantity came down. Read only when the draft quantity is below
   *  the stored one on a document whose reductions are recorded; the server
   *  refuses such a reduction without it. */
  reason: string;
}

/** Keyed by line id: a real uuid for a stored line, a `new:N` synthetic key for
 * one staged by Duplicate. Both kinds of row hold their edits the same way. */
export type LineDraftMap = Record<string, LineDraft>;

/** Marks an id as belonging to a staged copy rather than a stored row. A colon
 * cannot appear in a uuid, so the two can never be confused. */
const NEW_LINE_PREFIX = 'new:';

/** The id for the nth line staged this editing session. The ordinal is only for
 * telling two staged rows apart on screen and in a label; the server assigns the
 * real line_number when it appends. */
export const newLineId = (ordinal: number): string => `${NEW_LINE_PREFIX}${ordinal}`;

/** Is this a staged copy with no database row behind it? The one question that
 * decides whether an id may be sent as a removal or as an append. */
export const isNewLineId = (id: string): boolean => id.startsWith(NEW_LINE_PREFIX);

/** How a row is named in an error message and in an input's accessible name.
 * A staged copy has no line_number yet — the server assigns that on save — so
 * printing one would state a number the save may not honor. */
export function lineLabel(line: { id: string; line_number: number }): string {
  return isNewLineId(line.id)
    ? `New line ${line.id.slice(NEW_LINE_PREFIX.length)}`
    : `Line ${line.line_number}`;
}

const HEADER_TEXT_KEYS = [
  'terms',
  'quote_number',
  'ship_via',
  'ship_to_name',
  'ship_to_attn',
  'ship_to_address',
  'notes',
  'vendor_notes',
] as const;

const HEADER_DATE_KEYS = ['order_date', 'ship_date', 'expected_date'] as const;

/**
 * THIS MODULE HOLDS NO OPINION ABOUT WHAT IS EDITABLE.
 *
 * `po.editable` comes off the API ({ fields, lines, reasks_approval }), computed
 * by `editabilityFor()` in purchase-orders-service.js — the same call `update()`
 * gates on, so the API cannot advertise a field it would then refuse.
 *
 * DO NOT ADD A LOCAL MIRROR, even one a test keeps honest. A rule typed out
 * twice in two languages drifts the moment somebody adds a field in a hurry, and
 * the failure lands on a buyer whose typing is thrown away by a 409 the page
 * promised would not happen. Odoo and ERPNext both send field-level editability
 * down from the server for the same reason.
 *
 * If you are tempted to hardcode a field name here, add it to the server's list
 * instead.
 */

/** Will saving this patch disturb an approval already in flight? Uses the
 * server's `soft_fields`, the subset it says is harmless, so no field name is
 * spelled out on this side. */
export function patchReasksApproval(patch: UpdatePoInput, editable: PoEditable): boolean {
  if (!editable.reasks_approval) return false;
  // Moving a line is not money: an entry carrying only its id and a position
  // does not disturb the approver, and the server agrees (it never counts one
  // as hard work).
  const substantive = (patch.lines || []).some(
    (l) => !l.id || Object.keys(l).some((k) => k !== 'id' && k !== 'line_number')
  );
  if (substantive || patch.remove_line_ids?.length) return true;
  return Object.keys(patch).some(
    (k) => k !== 'lines' && k !== 'remove_line_ids' && !editable.soft_fields.includes(k)
  );
}

// ---------------------------------------------------------------------------
// Seeding a draft from the loaded PO
// ---------------------------------------------------------------------------

export function headerDraftFrom(po: PoDetail): HeaderDraft {
  return {
    order_date: po.order_date || '',
    ship_date: po.ship_date || '',
    expected_date: po.expected_date || '',
    terms: po.terms || '',
    terms_days: po.terms_days === null ? '' : String(po.terms_days),
    terms_basis: po.terms_basis || '',
    quote_number: po.quote_number || '',
    ship_via: po.ship_via || '',
    ship_to_name: po.ship_to_name || '',
    ship_to_attn: po.ship_to_attn || '',
    ship_to_address: po.ship_to_address || '',
    notes: po.notes || '',
    vendor_notes: po.vendor_notes || '',
    discount: discountOf(po),
  };
}

/** The stored discount as the entry row would have staged it, or null when
 * the order has none. Zero cents and no percent is "no discount". */
export function discountOf(po: Pick<PoDetail, 'discount_cents' | 'discount_pct' | 'discount_reason'>): StagedDiscount | null {
  if (po.discount_pct === null && !(po.discount_cents > 0)) return null;
  return {
    pct: po.discount_pct,
    cents: po.discount_pct === null ? po.discount_cents : 0,
    reason: po.discount_reason,
  };
}

/** One line of prose for the discount, the way the strip and the PDF say it:
 * "10%", "$50.00", with the reason after it when there is one. */
export function describeDiscount(discount: StagedDiscount, formatCents: (cents: number) => string): string {
  const figure = discount.pct !== null ? `${discount.pct}%` : formatCents(discount.cents);
  return discount.reason ? `${figure}, ${discount.reason}` : figure;
}

export function lineDraftFrom(line: PoLine): LineDraft {
  return {
    quantity_expected: String(line.quantity_expected),
    unit_cost: centsToDollarsInput(line.unit_cost_cents),
    expected_arrival_date: line.expected_arrival_date || '',
    notes: line.notes || '',
    reason: '',
  };
}

/** Is this draft a REDUCTION of the stored line, on a document that records
 *  one? A staged copy has no stored figure to come down from, so never. */
export function isRecordedReduction(line: PoLine, draft: LineDraft, editable: PoEditable): boolean {
  if (!editable.quantity_reductions_recorded || isNewLineId(line.id)) return false;
  // A half-typed or emptied field is not a reduction yet: Number('') is 0, and
  // asking for a reason on a box somebody has just cleared would be noise.
  const qty = Number(draft.quantity_expected);
  return Number.isInteger(qty) && qty > 0 && qty < line.quantity_expected;
}

/** What the line has NET: gross received less what went back to the vendor.
 *  The floor a reduction may not go under, and the server's own floor. */
export const netReceivedOf = (line: PoLine): number =>
  line.quantity_received - (line.quantity_returned || 0);

export function lineDraftsFrom(po: PoDetail): LineDraftMap {
  const out: LineDraftMap = {};
  for (const line of po.lines) out[line.id] = lineDraftFrom(line);
  return out;
}

/**
 * A staged copy of `source`, ready for the table to draw and for buildPatch to
 * append.
 *
 * It carries the SOURCE's identity fields — what the thing is — and nothing
 * about its history: quantity_received goes to zero and the status back to
 * pending, because the copy is a new order for more of the same part, not a
 * second claim on goods that already arrived. line_number is deliberately 0 and
 * never displayed: the server assigns the real one when it appends, and printing
 * a guess here would put a number on screen that the save may not honor.
 *
 * `line_total_cents` comes off the source too, so a band roll-up drawn before
 * the first keystroke is not a zero. Every cell that matters follows the draft.
 */
export function stagedCopyOf(source: PoLine, id: string): PoLine {
  return {
    ...source,
    id,
    line_number: 0,
    quantity_received: 0,
    status: 'pending',
    closed_at: null,
    cancelled_by: null,
    cancelled_reason: null,
    // The legacy hub's idempotency key. Carrying it over would make the copy
    // claim to be the same inbound shipment as its source.
    procurement_order_id: null,
    quantity_expected_original: null,
    quantity_change_reason: null,
  };
}

/**
 * A line the shared entry row staged (PoLineEntry), as a PoLine the table can
 * draw and buildPatch can append.
 *
 * Same shape as stagedCopyOf, different source: nothing is copied off a
 * neighbor, everything comes off what the buyer typed. The header lends what
 * the create form stamps per line at create time (PurchaseOrderForm
 * handleCreate): the expected date and the vendor's quote number. A service or
 * charge line's description lands in `notes`, because a PO line has ONE
 * free-text column and on a line with no item that column is what the line is.
 */
export function stagedFromEntry(staged: StagedLine, id: string, po: PoDetail): PoLine {
  const isStock = staged.lineType === 'stock';
  return {
    id,
    purchase_order_id: po.id,
    line_number: 0,
    line_type: staged.lineType,
    part_catalog_id: isStock ? staged.partCatalogId : null,
    revision: staged.revision,
    quantity_expected: staged.quantity,
    quantity_received: 0,
    quantity_returned: 0,
    quantity_expected_original: null,
    quantity_change_reason: null,
    vendor: po.vendor?.name ?? null,
    vendor_id: po.vendor_id,
    purchase_order_ref: po.quote_number || null,
    unit_cost_cents: staged.unitCostCents,
    line_total_cents: (staged.unitCostCents ?? 0) * staged.quantity,
    expected_arrival_date: po.expected_date || null,
    location_id: null,
    notes: isStock ? staged.notes : staged.lineDescription,
    status: 'pending',
    source: 'procurement_hub',
    procurement_order_id: null,
    created_by: null,
    created_at: '',
    updated_at: '',
    closed_at: null,
    cancelled_by: null,
    cancelled_reason: null,
    sku: staged.sku,
    part_description: staged.description ?? null,
    base_name: staged.base_name ?? null,
    display_name: staged.display_name ?? null,
    variant_detail: staged.variant_detail ?? null,
    family_key: null,
    family_label: null,
  };
}

// ---------------------------------------------------------------------------
// Live totals — what the draft WOULD cost, so an edited quantity never sits
// next to a line total computed from the old one.
// ---------------------------------------------------------------------------

export function draftLineTotalCents(draft: LineDraft): number | null {
  const qty = Number(draft.quantity_expected);
  const cents = dollarsToCents(draft.unit_cost);
  if (!Number.isFinite(qty) || cents === null) return null;
  return Math.round(qty * cents);
}

/** Subtotal over the lines that would survive the save. Matches the backend's
 * computeSubtotalCents, which sums quantity_expected * unit_cost_cents over
 * every line on the PO. */
export function draftSubtotalCents(
  po: PoDetail,
  lineDrafts: LineDraftMap,
  removedLineIds: string[],
  addedLines: PoLine[] = []
): number {
  const removed = new Set(removedLineIds);
  // Staged copies count: the server recomputes the subtotal AFTER appending
  // them, so a total that left them out would be the figure on screen
  // disagreeing with the figure the save produces.
  return [...po.lines, ...addedLines].reduce((sum, line) => {
    if (removed.has(line.id)) return sum;
    const draft = lineDrafts[line.id];
    if (!draft) return sum + line.line_total_cents;
    return sum + (draftLineTotalCents(draft) ?? 0);
  }, 0);
}

/** What the draft's discount comes to against the draft's subtotal. A percent
 * follows the lines as they are being edited, which is exactly what the server
 * will compute on save; an amount stands. */
export function draftDiscountCents(discount: StagedDiscount | null, subtotalCents: number): number {
  if (!discount) return 0;
  return discount.pct !== null ? Math.round((subtotalCents * discount.pct) / 100) : discount.cents;
}

/** Subtotal less the discount: the figure the approval threshold and the
 * approver's DM are about. Never below zero on screen; the server refuses the
 * save that would get there (discount_exceeds_subtotal). */
export function draftTotalCents(subtotalCents: number, discount: StagedDiscount | null): number {
  return subtotalCents - draftDiscountCents(discount, subtotalCents);
}

// ---------------------------------------------------------------------------
// Validation — the rules the API enforces, checked before the round trip so a
// typo lands on the field the user is looking at.
// ---------------------------------------------------------------------------

/** First problem found, phrased for a toast, or null when the draft is sendable.
 * Line numbers (not ids) so the message points at a row on screen. */
export function validateDrafts(
  po: PoDetail,
  headerDraft: HeaderDraft,
  lineDrafts: LineDraftMap,
  removedLineIds: string[],
  addedLines: PoLine[] = []
): string | null {
  if (!headerDraft.order_date) return 'Order date cannot be empty.';
  if (headerDraft.terms_days.trim() !== '') {
    const days = Number(headerDraft.terms_days);
    if (!Number.isInteger(days) || days < 0 || days > 365) {
      return 'Net days must be a whole number, 0 to 365.';
    }
  }
  // Half a clock computes nothing and shows as nothing, so it is refused here
  // rather than stored: both halves, or neither.
  if ((headerDraft.terms_days.trim() === '') !== (headerDraft.terms_basis === '')) {
    return headerDraft.terms_basis === ''
      ? 'Say what the net days count from, or clear them.'
      : 'Net days are needed for that payment clock, or clear what it counts from.';
  }

  const removed = new Set(removedLineIds);
  // Staged copies are on the right side of this count, matching the server: it
  // refuses an empty PO only AFTER the appends, so removing every stored line
  // and duplicating a replacement in one save is legal. The end state is what
  // has to be legal, not each step.
  const surviving = [...po.lines, ...addedLines].filter((line) => !removed.has(line.id));
  if (surviving.length === 0) {
    return 'A purchase order needs at least one line. Cancel it instead of emptying it.';
  }

  for (const line of surviving) {
    const draft = lineDrafts[line.id];
    if (!draft) continue;
    const qty = Number(draft.quantity_expected);
    if (!Number.isInteger(qty) || qty <= 0) {
      return `${lineLabel(line)}: quantity must be a whole number above zero.`;
    }
    // The short-fill tier, mirrored from the server's refusals so they land on
    // the row before the round trip: down needs a reason and stops at what
    // arrived; up is not an edit once the order is out.
    if (po.editable.quantity_reductions_recorded && !isNewLineId(line.id)) {
      if (qty > line.quantity_expected && !po.editable.lines) {
        return `${lineLabel(line)}: a sent order's quantity can only come down. To order more, raise a new purchase order.`;
      }
      if (qty < line.quantity_expected) {
        const net = netReceivedOf(line);
        if (qty < net) {
          return `${lineLabel(line)}: ${net} already arrived, so the quantity cannot go below that.`;
        }
        if (!draft.reason.trim()) {
          return `${lineLabel(line)}: say why the quantity came down.`;
        }
      }
    }
    const trimmedCost = draft.unit_cost.trim();
    if (trimmedCost) {
      const cents = dollarsToCents(trimmedCost);
      if (cents === null || cents < 0) {
        return `${lineLabel(line)}: unit cost must be a dollar amount, zero or more.`;
      }
    }
  }

  // AFTER the per-line checks: a half-typed quantity counts as 0 in the
  // subtotal, and the sentence has to name the box that is wrong, not the
  // discount it happened to shrink the order under. Mirrors the server's
  // discount_exceeds_subtotal, so a line edit that no longer covers a dollar
  // discount is caught on the page rather than as a 409.
  const subtotal = draftSubtotalCents(po, lineDrafts, removedLineIds, addedLines);
  if (draftDiscountCents(headerDraft.discount, subtotal) > subtotal) {
    return 'The discount is larger than the lines. Lower it or remove it.';
  }

  return null;
}

// ---------------------------------------------------------------------------
// Line order
// ---------------------------------------------------------------------------
//
// The page holds the order as an array of ids, staged copies included. A save
// sends positions ONLY when that order differs from what the server would
// produce on its own, so a save that only changed a quantity carries no
// numbers and the vendor's paper keeps matching the document.

/** The lines that sink to the bottom of the document. */
export const isChargeLine = (line: { line_type: string }): boolean => line.line_type === 'charge';

/** The stored lines' ids as the page shows them: charges last, otherwise in
 * document order. Same rule as the server's normalizeLineOrder. */
export function pinnedLineIds(lines: PoLine[]): string[] {
  return [...lines.filter((l) => !isChargeLine(l)), ...lines.filter(isChargeLine)].map((l) => l.id);
}

/** Would saving this order change any line's number? Compared against what
 * the server does with no positions at all: stored lines keep their order and
 * each staged line lands at the end of its own block. */
export function lineOrderChanged(
  po: PoDetail,
  addedLines: PoLine[],
  lineOrder: string[],
  removedLineIds: string[]
): boolean {
  const byId = new Map([...po.lines, ...addedLines].map((l) => [l.id, l]));
  const sinks = (id: string) => {
    const line = byId.get(id);
    return !!line && isChargeLine(line);
  };
  const survivors = lineOrder.filter((id) => !removedLineIds.includes(id));
  let baseline = pinnedLineIds(po.lines).filter((id) => !removedLineIds.includes(id));
  // In the order the appends are SENT (buildPatch walks `addedLines`), not the
  // page's order: two staged lines swapped on screen would otherwise read as
  // "already where the server puts them" and the swap would be lost.
  for (const line of addedLines) {
    if (survivors.includes(line.id)) baseline = insertPinned(baseline, line.id, sinks);
  }
  return survivors.length !== baseline.length || survivors.some((id, i) => id !== baseline[i]);
}

/** Each surviving line's position, counting from 1, in the given order. */
export function linePositions(lineOrder: string[], removedLineIds: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  lineOrder.filter((id) => !removedLineIds.includes(id)).forEach((id, i) => {
    out[id] = i + 1;
  });
  return out;
}

// ---------------------------------------------------------------------------
// The diff
// ---------------------------------------------------------------------------

/** Build the PATCH body for whatever actually changed, or null when nothing
 * did — the caller uses that to leave edit mode without a request, rather than
 * eating the API's no_patchable_fields 400. */
export function buildPatch(
  po: PoDetail,
  headerDraft: HeaderDraft,
  lineDrafts: LineDraftMap,
  removedLineIds: string[],
  addedLines: PoLine[] = [],
  lineOrder?: string[]
): UpdatePoInput | null {
  const patch: UpdatePoInput = {};
  // Positions ride on EVERY surviving line once any line moved, staged ones
  // included: the server checks the whole set for collisions, and a partial
  // set could collide with a number it was not told about.
  const positions = lineOrder && lineOrderChanged(po, addedLines, lineOrder, removedLineIds)
    ? linePositions(lineOrder, removedLineIds)
    : null;

  for (const key of HEADER_TEXT_KEYS) {
    const next = headerDraft[key].trim();
    if (next !== (po[key] || '')) patch[key] = next || null;
  }

  for (const key of HEADER_DATE_KEYS) {
    const next = headerDraft[key];
    if (next === (po[key] || '')) continue;
    // order_date is NOT NULL server-side. Blanking it is not an edit anyone can
    // mean, and validateDrafts already refused it — belt and suspenders.
    if (key === 'order_date' && !next) continue;
    patch[key] = next || null;
  }

  // The clock: a number and a vocabulary word, compared as the strings the
  // draft holds and sent as what the column holds.
  const nextDays = headerDraft.terms_days.trim();
  if (nextDays !== (po.terms_days === null ? '' : String(po.terms_days))) {
    patch.terms_days = nextDays === '' ? null : Number(nextDays);
  }
  if (headerDraft.terms_basis !== (po.terms_basis || '')) {
    patch.terms_basis = isPaymentTermsBasis(headerDraft.terms_basis) ? headerDraft.terms_basis : null;
  }

  // The discount travels WHOLE when any part of it moved, because that is how
  // the server reads it: a patch naming discount_pct or discount_cents states
  // the discount as it now is. Removing it is zero cents and no reason.
  const stored = discountOf(po);
  const next = headerDraft.discount;
  const discountMoved = (stored === null) !== (next === null)
    || (stored !== null && next !== null && (
      stored.pct !== next.pct || stored.cents !== next.cents || (stored.reason || null) !== (next.reason || null)
    ));
  if (discountMoved) {
    patch.discount_pct = next ? next.pct : null;
    patch.discount_cents = next && next.pct === null ? next.cents : 0;
    patch.discount_reason = next ? next.reason : null;
  }

  const removed = new Set(removedLineIds);
  const lines: PoLineInput[] = [];

  for (const line of po.lines) {
    if (removed.has(line.id)) continue;
    const draft = lineDrafts[line.id];
    if (!draft) continue;

    const changed: PoLineInput = { id: line.id };
    let touched = false;

    // Compare the values that would be WRITTEN, not the strings on screen:
    // retyping 41.50 as 41.5 is the same cost and should not count as an edit.
    const nextQty = Number(draft.quantity_expected);
    if (nextQty !== line.quantity_expected) {
      changed.quantity_expected = nextQty;
      // The reason rides with the reduction it pays for, and only then: sent
      // beside an increase or on a draft it would be a key the server ignores.
      if (isRecordedReduction(line, draft, po.editable)) changed.reason = draft.reason.trim();
      touched = true;
    }

    const nextCost = dollarsToCents(draft.unit_cost);
    if (nextCost !== line.unit_cost_cents) {
      changed.unit_cost_cents = nextCost;
      touched = true;
    }

    const nextArrival = draft.expected_arrival_date || null;
    if (nextArrival !== line.expected_arrival_date) {
      changed.expected_arrival_date = nextArrival;
      touched = true;
    }

    // Compared TRIMMED on both sides: a stored note with a trailing newline
    // (pasted from an email) would otherwise ship `notes` on every save, and on
    // an issued order the server refuses that key, so the row could never save.
    const nextNotes = draft.notes.trim() || null;
    if (nextNotes !== (line.notes?.trim() || null)) {
      changed.notes = nextNotes;
      touched = true;
    }

    if (positions && positions[line.id] !== undefined) {
      changed.line_number = positions[line.id];
      touched = true;
    }

    if (touched) lines.push(changed);
  }

  // ── staged copies, appended ────────────────────────────────────────────────
  //
  // NO `id`, which is the whole mechanism: PurchaseOrdersService.update appends
  // any entry that arrives without one. Unlike a patch entry, this sends the
  // line WHOLE rather than as a diff — there is no stored row to leave the
  // unsent fields alone, so anything omitted here would be created as null.
  //
  // The identity fields come off the copied row and the rest off the draft,
  // which is the split that makes Duplicate mean "another one of these, on my
  // terms": what the line IS is not editable on this page, what it costs and
  // when it lands is.
  for (const line of addedLines) {
    if (removedLineIds.includes(line.id)) continue;
    const draft = lineDrafts[line.id];
    if (!draft) continue;
    lines.push({
      part_catalog_id: line.part_catalog_id,
      line_type: line.line_type,
      revision: line.revision,
      location_id: line.location_id,
      purchase_order_ref: line.purchase_order_ref,
      quantity_expected: Number(draft.quantity_expected),
      unit_cost_cents: dollarsToCents(draft.unit_cost),
      expected_arrival_date: draft.expected_arrival_date || null,
      notes: draft.notes.trim() || null,
      ...(positions && positions[line.id] !== undefined ? { line_number: positions[line.id] } : {}),
    });
  }

  if (lines.length > 0) patch.lines = lines;
  // Synthetic ids are filtered out rather than trusted not to be here: a staged
  // copy has no database row, and sending its id as a removal would 404 the
  // whole save. Discarding one drops it from `addedLines` on the page, so in
  // practice this never fires — it is the guard, not the mechanism.
  const realRemovals = removedLineIds.filter((id) => !isNewLineId(id));
  if (realRemovals.length > 0) patch.remove_line_ids = realRemovals;

  return Object.keys(patch).length > 0 ? patch : null;
}
